import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Info } from "lucide-react";
import { BrutalCard, SectionTitle, EmptyState } from "@/components/Brutal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import TaskCard from "@/components/TaskCard";
import { initials } from "@/components/AiComponents";
import { dueMeta, WEEKDAYS, userColor, pinTimeLabel, pindateColor, PINDATE_DAYPARTS, PINDATE_NEUTRAL } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useModuleGuard } from "@/lib/useModuleGuard";
import { toast } from "sonner";
import api from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };

export default function Rooster() {
  useModuleGuard("rooster");
  const { user } = useAuth();
  const tr = useT();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(startOfWeek(new Date()));
  const [mineOnly, setMineOnly] = useState(false);
  const [pinsOnly, setPinsOnly] = useState(false);
  const [personFilter, setPersonFilter] = useState("");
  const [dayKey, setDayKey] = useState(null);
  const [pinDetail, setPinDetail] = useState(null);
  const [quickDate, setQuickDate] = useState(null);
  const [quickForm, setQuickForm] = useState({ title: "", event_time: "", event_end_time: "", description: "" });
  const [quickBusy, setQuickBusy] = useState(false);
  const [pins, setPins] = useState([]);
  const [viewInit, setViewInit] = useState(false);

  const load = async () => {
    const [t, u, g, p] = await Promise.all([api.get("/tasks"), api.get("/users"), api.get("/groups"), api.get("/content/pindates")]);
    setTasks(t.data); setUsers(u.data); setGroups(g.data); setPins((p.data || []).filter((x) => x.event_date));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (viewInit) return;
    api.get("/settings").then((r) => {
      if (r.data?.default_calendar_view === "month") { setView("month"); setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); }
      setViewInit(true);
    }).catch(() => setViewInit(true));
  }, [viewInit]);

  const postpone = async (taskId, dateStr) => {
    try { await api.post(`/tasks/${taskId}/postpone`, { new_date: dateStr }); await load(); toast.success(tr("Taak verplaatst 📆")); }
    catch (e) { toast.error(e.response?.data?.detail || tr("Verplaatsen mislukt")); }
  };
  const allowDrop = (e) => e.preventDefault();
  const onDrop = (e, dateStr) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) postpone(id, dateStr); };
  const dragStart = (e, id) => { e.stopPropagation(); e.dataTransfer.setData("text/plain", id); e.dataTransfer.effectAllowed = "move"; };

  const openQuickPin = (dateKey) => { setQuickForm({ title: "", event_time: "", event_end_time: "", description: "" }); setQuickDate(dateKey); };
  const saveQuickPin = async () => {
    if (!quickForm.title.trim()) return toast.error(tr("Titel verplicht"));
    setQuickBusy(true);
    try {
      await api.post("/content/pindates", { title: quickForm.title.trim(), event_date: quickDate, event_time: quickForm.event_time || null, event_end_time: quickForm.event_end_time || null, description: quickForm.description || null, photos: [], reminder_enabled: true });
      toast.success(tr("Pin date toegevoegd 📌"));
      setQuickDate(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Toevoegen mislukt")); }
    finally { setQuickBusy(false); }
  };

  const dateFor = (t) => t.next_date || iso(new Date(t.created_at || Date.now()));
  const activePerson = mineOnly ? user.user_id : (personFilter || null);
  const taskMatchesPerson = (t) => !activePerson || t.assignee_ids?.includes(activePerson) || t.assignee_id === activePerson;
  const pinMatchesPerson = (p) => !activePerson || !(p.recipient_ids?.length) || p.recipient_ids.includes(activePerson);
  const visible = pinsOnly ? [] : tasks.filter((t) => t.status !== "klaar" && taskMatchesPerson(t));
  const byDate = {};
  visible.forEach((t) => { const d = dateFor(t); (byDate[d] = byDate[d] || []).push(t); });
  const dayTasks = dayKey ? (pinsOnly ? [] : tasks.filter((t) => dateFor(t) === dayKey && taskMatchesPerson(t))) : [];
  const pinsByDate = {};
  pins.filter(pinMatchesPerson).forEach((p) => { (pinsByDate[p.event_date] = pinsByDate[p.event_date] || []).push(p); });
  const dayPins = dayKey ? (pinsByDate[dayKey] || []) : [];

  const today = iso(new Date());
  const shift = (n) => { const x = new Date(cursor); x.setDate(x.getDate() + n); setCursor(view === "week" ? startOfWeek(x) : new Date(x.getFullYear(), x.getMonth() + (n > 0 ? 1 : -1), 1)); };

  let days = [];
  if (view === "week") {
    for (let i = 0; i < 7; i++) { const d = new Date(cursor); d.setDate(d.getDate() + i); days.push(d); }
  } else {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    for (let i = 0; i < 42; i++) { const d = new Date(gridStart); d.setDate(d.getDate() + i); days.push(d); }
  }

  const label = view === "week"
    ? `Week van ${cursor.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}`
    : cursor.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });

  const userById = {};
  users.forEach((u) => { userById[u.user_id] = u; });
  const assigneesOf = (t) => ((t.assignee_ids && t.assignee_ids.length) ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : [])).map((id) => userById[id]).filter(Boolean);
  const Ava = ({ u, cls = "w-5 h-5 text-[9px]" }) => {
    const c = userColor(u.user_id);
    return (
      <span title={u.name} style={{ background: c.bg, color: c.text }} className={`inline-flex items-center justify-center rounded-full font-black overflow-hidden shrink-0 ring-1 ring-white ${cls}`}>
        {u.avatar_url ? <img src={u.avatar_url.startsWith("http") ? u.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${u.avatar_url}`} alt="" className="w-full h-full object-cover" /> : initials(u.name)}
      </span>
    );
  };
  const Assignees = ({ t, cls }) => {
    const a = assigneesOf(t);
    if (!a.length) return null;
    return <span className="flex -space-x-1.5 shrink-0" data-testid={`rooster-task-assignees-${t.id}`}>{a.slice(0, 3).map((u) => <Ava key={u.user_id} u={u} cls={cls} />)}{a.length > 3 && <span className="text-[9px] font-bold self-center ml-1">+{a.length - 3}</span>}</span>;
  };

  const TaskChip = ({ t }) => {
    const m = dueMeta(t.due_when);
    return <div data-testid={`rooster-task-${t.id}`} className={`text-[10px] font-bold rounded px-1 py-0.5 mb-0.5 flex items-center gap-1 pointer-events-none ${m.color} ${m.text || "text-brand-ink"}`} title={t.title}><Assignees t={t} cls="w-4 h-4 text-[8px]" /><span className="truncate">{t.title}</span></div>;
  };
  const PinChip = ({ p }) => { const c = pindateColor(p); const glow = p.event_date === today; return (
    <div data-testid={`rooster-pin-${p.id}`} className={`text-[10px] font-bold rounded px-1 py-0.5 mb-0.5 truncate pointer-events-none ${c.cls} ${c.text} ${glow ? "k-today-glow" : ""}`} style={glow ? { "--glow": `color-mix(in srgb, ${c.hex} 60%, transparent)` } : undefined} title={p.title}>📌 {pinTimeLabel(p) ? pinTimeLabel(p) + " " : ""}{p.title}</div>
  ); };
  const PinLegend = () => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="rooster-pin-legend">
      <span className="font-bold text-brand-ink/50">📌 {tr("Pin dates")}:</span>
      {[...PINDATE_DAYPARTS, PINDATE_NEUTRAL].map((d) => (
        <span key={d.value} className="inline-flex items-center gap-1 font-bold text-brand-ink/70">
          <span className={`w-3 h-3 rounded-full ring-1 ring-brand-ink/10 ${d.cls}`} />{tr(d.label)}
        </span>
      ))}
      <span title={tr("Een exacte tijd krijgt automatisch de kleur van het bijbehorende dagdeel (ochtend, middag of avond).")} data-testid="rooster-pin-legend-info" className="inline-flex items-center cursor-help text-brand-ink/40"><Info className="w-3.5 h-3.5" strokeWidth={2.5} /></span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="rooster-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <SectionTitle sub={tr("Sleep taken naar een andere dag om uit te stellen")}>{tr("Rooster")}</SectionTitle>
        <div className="flex gap-2">
          <button data-testid="rooster-week" onClick={() => { setView("week"); setCursor(startOfWeek(new Date())); }} className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${view === "week" ? "bg-brand-violet text-white" : "bg-white"}`}>{tr("Week")}</button>
          <button data-testid="rooster-month" onClick={() => { setView("month"); setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); }} className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${view === "month" ? "bg-brand-violet text-white" : "bg-white"}`}>{tr("Maand")}</button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button data-testid="rooster-prev" onClick={() => shift(-7)} className="brutal-sm brutal-press rounded-full p-2 bg-white"><ChevronLeft className="w-5 h-5" strokeWidth={2.5} /></button>
        <p className="font-head font-black text-lg">{label}</p>
        <button data-testid="rooster-next" onClick={() => shift(7)} className="brutal-sm brutal-press rounded-full p-2 bg-white"><ChevronRight className="w-5 h-5" strokeWidth={2.5} /></button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="rooster-filters">
        <button data-testid="rooster-mine" onClick={() => { const n = !mineOnly; setMineOnly(n); if (n) setPersonFilter(""); }} className={`brutal-sm brutal-press rounded-full px-4 py-1.5 text-sm font-bold ${mineOnly ? "bg-brand-ink text-white" : "bg-white"}`}>{tr("Alleen mijn taken")}</button>
        <button data-testid="rooster-pins-only" onClick={() => setPinsOnly(!pinsOnly)} className={`brutal-sm brutal-press rounded-full px-4 py-1.5 text-sm font-bold ${pinsOnly ? "bg-brand-teal text-brand-ink" : "bg-white"}`}>📌 {tr("Alleen pin dates")}</button>

        {users.length > 0 && users.length <= 8 ? (
          <div className="flex flex-wrap gap-2" data-testid="rooster-person-chips">
            {users.map((u) => { const c = userColor(u.user_id); const on = personFilter === u.user_id && !mineOnly; return (
              <button key={u.user_id} type="button" data-testid={`rooster-person-${u.user_id}`}
                onClick={() => { setPersonFilter(on ? "" : u.user_id); setMineOnly(false); }}
                className={`inline-flex items-center gap-1.5 brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold ${on ? "text-white" : "bg-white"}`}
                style={on ? { background: c.bg } : {}}>
                <span className="w-3 h-3 rounded-full ring-1 ring-white shrink-0" style={{ background: c.bg }} />{u.name}
              </button>
            ); })}
          </div>
        ) : users.length > 8 ? (
          <select data-testid="rooster-person-select" value={mineOnly ? "" : personFilter} onChange={(e) => { setPersonFilter(e.target.value); setMineOnly(false); }}
            className="brutal-sm rounded-full px-4 py-1.5 text-sm font-bold bg-white outline-none">
            <option value="">👥 {tr("Alle personen")}</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
        ) : null}
      </div>

      <PinLegend />

      {view === "week" ? (
        <div className="space-y-2">
          {days.map((d) => {
            const key = iso(d);
            const list = byDate[key] || [];
            return (
              <BrutalCard key={key} data-testid={`rooster-day-${key}`} onClick={() => setDayKey(key)} onDragOver={allowDrop} onDrop={(e) => onDrop(e, key)} className={`cursor-pointer ${key === today ? "border-brand-violet" : ""}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-head font-bold">{WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}/{d.getMonth() + 1}</p>
                  {key === today && <span className="text-xs font-bold bg-brand-violet text-white rounded-full px-2 py-0.5">{tr("Vandaag")}</span>}
                </div>
                {(pinsByDate[key] || []).map((p) => { const c = pindateColor(p); const glow = p.event_date === today; return (
                  <div key={p.id} onClick={(e) => { e.stopPropagation(); setPinDetail(p); }} data-testid={`rooster-pin-${p.id}`} className={`brutal-sm rounded-lg px-2 py-1 text-sm font-bold mb-1 cursor-pointer ${c.cls} ${c.text} ${glow ? "k-today-glow" : ""}`} style={glow ? { "--glow": `color-mix(in srgb, ${c.hex} 60%, transparent)` } : undefined}>📌 {pinTimeLabel(p) ? pinTimeLabel(p) + " " : ""}{p.title}</div>
                ); })}
                {list.length === 0 && (pinsByDate[key] || []).length === 0 ? <p className="text-xs text-brand-ink/40">{tr("Niks gepland")}</p> :
                  list.map((t) => <div key={t.id} draggable onDragStart={(e) => dragStart(e, t.id)} onClick={(e) => e.stopPropagation()} data-testid={`rooster-task-${t.id}`} className={`brutal-sm rounded-lg px-2 py-1 text-sm font-bold mb-1 flex items-center justify-between gap-2 cursor-grab active:cursor-grabbing ${dueMeta(t.due_when).color} ${dueMeta(t.due_when).text || "text-brand-ink"}`}><span className="truncate">{t.title}</span><Assignees t={t} cls="w-5 h-5 text-[9px]" /></div>)}
              </BrutalCard>
            );
          })}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-bold text-brand-ink/50">{w.slice(0, 2)}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const key = iso(d);
              const list = byDate[key] || [];
              const dim = d.getMonth() !== cursor.getMonth();
              return (
                <div key={key} data-testid={`rooster-cell-${key}`} onClick={() => setDayKey(key)} onDragOver={allowDrop} onDrop={(e) => onDrop(e, key)} className={`brutal-sm rounded-lg p-1 min-h-[64px] cursor-pointer ${dim ? "opacity-40 bg-white" : "bg-white"} ${key === today ? "border-2 border-brand-violet" : ""}`}>
                  <p className="text-[11px] font-bold">{d.getDate()}</p>
                  {(pinsByDate[key] || []).slice(0, 2).map((p) => <PinChip key={p.id} p={p} />)}
                  {list.slice(0, 3).map((t) => <TaskChip key={t.id} t={t} />)}
                  {list.length > 3 && <p className="text-[10px] font-bold text-brand-ink/50">+{list.length - 3}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!dayKey} onOpenChange={(v) => !v && setDayKey(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[85vh] overflow-auto" data-testid="rooster-day-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">
            {dayKey && new Date(dayKey).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}
          </DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Taken van deze dag; vink af of heropen direct.</DialogDescription>
          <button data-testid="rooster-quick-pin-btn" onClick={() => openQuickPin(dayKey)} className="w-full brutal-sm brutal-press rounded-xl px-3 py-2.5 bg-brand-teal text-brand-ink font-head font-bold flex items-center justify-center gap-1.5 mb-3">
            <Plus className="w-4 h-4" strokeWidth={2.5} />{tr("Pin date toevoegen")}
          </button>
          {dayPins.length > 0 && (
            <div className="space-y-2 mb-3">
              {dayPins.map((p) => { const c = pindateColor(p); const glow = p.event_date === today; return (
                <div key={p.id} onClick={() => setPinDetail(p)} data-testid={`rooster-day-pin-${p.id}`} className={`brutal-sm rounded-lg p-3 bg-white flex items-center gap-2 cursor-pointer ${glow ? "k-today-glow" : ""}`} style={{ borderLeft: `6px solid ${c.hex}`, ...(glow ? { "--glow": `color-mix(in srgb, ${c.hex} 60%, transparent)` } : {}) }}>
                  <span className="text-lg">📌</span>
                  <div>
                    <p className="font-bold">{p.title}</p>
                    {p.event_time && <p className="text-xs text-brand-ink/60">{p.event_time}{p.event_end_time ? `–${p.event_end_time}` : ""}</p>}
                    {p.reminder_enabled === false && <p className="text-[10px] font-bold text-brand-ink/50">{tr("Alleen weergave — geen herinnering")}</p>}
                  </div>
                </div>
              ); })}
            </div>
          )}
          {dayTasks.length === 0 && dayPins.length === 0 ? (
            <EmptyState icon={CalendarDays} title={tr("Niks gepland")} text={tr("Op deze dag staat niks gepland.")} />
          ) : (
            <div className="space-y-3">
              {dayTasks.map((t) => <TaskCard key={t.id} task={t} users={users} groups={groups} onChange={load} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinDetail} onOpenChange={(v) => !v && setPinDetail(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[85vh] overflow-auto" data-testid="rooster-pin-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl leading-tight">📌 {pinDetail?.title}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Details van deze pin date.</DialogDescription>
          {pinDetail && (() => {
            const gal = (pinDetail.photos && pinDetail.photos.length) ? pinDetail.photos : (pinDetail.image_url ? [pinDetail.image_url] : []);
            const src = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
            const rnames = (pinDetail.recipient_ids || []).map((r) => users.find((u) => u.user_id === r)?.name).filter(Boolean);
            let when = String(pinDetail.event_date || "");
            try { when = new Date(pinDetail.event_date).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }); } catch { }
            return (
              <div className="space-y-3">
                {gal.length > 0 && (
                  <div className={gal.length === 1 ? "" : "grid grid-cols-2 gap-2"}>
                    {gal.map((u, i) => <img key={i} src={src(u)} alt="" className="w-full h-40 object-cover brutal-sm rounded-xl" data-testid={`rooster-pin-photo-${i}`} />)}
                  </div>
                )}
                <p className="font-bold text-brand-blue flex items-center gap-1.5"><CalendarDays className="w-4 h-4" strokeWidth={2.5} />{when}{pinTimeLabel(pinDetail) ? ` · ${pinTimeLabel(pinDetail)}` : ""}</p>
                {pinDetail.description && <p className="text-sm whitespace-pre-wrap">{pinDetail.description}</p>}
                {rnames.length > 0 && <p className="text-sm text-brand-ink/70"><span className="font-bold">{tr("Voor")}:</span> {rnames.join(", ")}</p>}
                {pinDetail.reminder_enabled === false && <p className="text-xs font-bold text-brand-ink/50">{tr("Alleen weergave — geen herinnering")}</p>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!quickDate} onOpenChange={(v) => !v && setQuickDate(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid="rooster-quick-pin-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2">📌 {tr("Nieuwe pin date")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Snel een pin date plannen op deze dag.</DialogDescription>
          <p className="text-sm font-bold text-brand-blue -mt-1">{quickDate && new Date(quickDate).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</p>
          <input data-testid="quick-pin-title" value={quickForm.title} onChange={(e) => setQuickForm({ ...quickForm, title: e.target.value })} placeholder={tr("Titel (bv. Tandarts)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-bold">{tr("Van")}</label><input data-testid="quick-pin-time" type="time" value={quickForm.event_time} onChange={(e) => setQuickForm({ ...quickForm, event_time: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none mt-1" /></div>
            <div><label className="text-xs font-bold">{tr("Tot")}</label><input data-testid="quick-pin-endtime" type="time" value={quickForm.event_end_time} onChange={(e) => setQuickForm({ ...quickForm, event_end_time: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none mt-1" /></div>
          </div>
          <textarea data-testid="quick-pin-desc" value={quickForm.description} onChange={(e) => setQuickForm({ ...quickForm, description: e.target.value })} rows={2} placeholder={tr("Omschrijving (optioneel)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <button data-testid="quick-pin-save" disabled={quickBusy} onClick={saveQuickPin} className="w-full brutal brutal-press rounded-xl py-3 bg-brand-violet text-white font-head font-bold disabled:opacity-50">{quickBusy ? tr("Bezig…") : tr("Opslaan")}</button>
          <p className="text-xs text-brand-ink/50 text-center">{tr("Meer opties (foto's, voor wie, herinnering)? Ga naar Meer → Pin Dates.")}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
